import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Decorative } from "@faire/design-tokens";
import { FormattedText } from "@faire/retailer-visitor-shared/components/FormattedText";
import { EcoFriendlyC2PageImageSlider } from "@faire/retailer-visitor-shared/components/LayoutElementRenderer/__internal__/Components/EcoFriendlyC2PageImageSlider";
import { Context } from "@faire/retailer-visitor-shared/components/LayoutElementRenderer/__internal__/Context";
import { useMilestone } from "@faire/retailer-visitor-shared/stores/domain/useMilestone";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { isNotUndefined } from "@faire/web--source/common/typescriptUtils";
import { Button } from "@faire/web--source/slate/Button";
import { ResponsibleConsumption } from "@faire/web--source/slate/icons/foundation/ResponsibleConsumption";
import { ShareHeart } from "@faire/web--source/slate/icons/foundation/ShareHeart";
import { Sprout } from "@faire/web--source/slate/icons/foundation/Sprout";
import { Column, Flex } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography, TypographyStyles } from "@faire/web--source/slate/Typography";
import { useState } from "@faire/web--source/ui/hooks/useState";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { trackEcoFriendlyDefinitionBannerShowMoreButtonClick } from "@faire/web-api--source/events/ecoFriendly/click/definitionBannerShowMoreButton";
import { HAS_SEEN_ECO_FRIENDLY_CATEGORY_PAGE } from "@faire/web-api--source/indigofair/data/IUserMilestone/RetailerMilestone/HAS_SEEN_ECO_FRIENDLY_CATEGORY_PAGE";
import isUndefined from "lodash/isUndefined";
import React, { useEffect, useRef } from "react";
import styled from "styled-components";
var getBannerHeaderMessage = function(headerText, descriptionText) {
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Typography, {
                variant: "pageHeaderSerifRegular",
                children: headerText
            }),
            /*#__PURE__*/ _jsx(StyledTypography, {
                children: /*#__PURE__*/ _jsx(FormattedText, {
                    text: descriptionText
                })
            })
        ]
    });
};
var getBannerBulletMessageUpdate = function(collapsed) {
    return /*#__PURE__*/ _jsxs(AnimatedComponent, {
        $collapsed: collapsed,
        children: [
            /*#__PURE__*/ _jsxs(ResponsiveContainer, {
                children: [
                    /*#__PURE__*/ _jsx(IconWrapper, {
                        children: /*#__PURE__*/ _jsx(Sprout, {
                            fontSize: "20px",
                            color: Decorative.icon.orange
                        })
                    }),
                    /*#__PURE__*/ _jsxs(Typography, {
                        variant: "paragraphSansRegular",
                        children: [
                            /*#__PURE__*/ _jsx("b", {
                                children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                                    id: "HY6DE7",
                                    defaultMessage: [
                                        {
                                            type: 0,
                                            value: "Product Materials"
                                        }
                                    ]
                                })
                            }),
                            /*#__PURE__*/ _jsx("br", {}),
                            /*#__PURE__*/ _jsx(StrictLocalMsg, {
                                id: "1S7tyq",
                                defaultMessage: [
                                    {
                                        type: 0,
                                        value: "Products are made with recycled, upcycled, vegan, organic, or plastic-free materials, or are refill products."
                                    }
                                ]
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(ResponsiveContainer, {
                children: [
                    /*#__PURE__*/ _jsx(IconWrapper, {
                        children: /*#__PURE__*/ _jsx(ResponsibleConsumption, {
                            fontSize: "20px",
                            color: Decorative.icon.orange
                        })
                    }),
                    /*#__PURE__*/ _jsxs(Typography, {
                        variant: "paragraphSansRegular",
                        children: [
                            /*#__PURE__*/ _jsx("b", {
                                children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                                    id: "K5Lx/f",
                                    defaultMessage: [
                                        {
                                            type: 0,
                                            value: "Packaging"
                                        }
                                    ]
                                })
                            }),
                            /*#__PURE__*/ _jsx("br", {}),
                            /*#__PURE__*/ _jsx(StrictLocalMsg, {
                                id: "x5SFL9",
                                defaultMessage: [
                                    {
                                        type: 0,
                                        value: "Product packaging is reduced-weight or is made with recycled, recyclable, or plastic-free materials."
                                    }
                                ]
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(ResponsiveContainer, {
                children: [
                    /*#__PURE__*/ _jsx(IconWrapper, {
                        children: /*#__PURE__*/ _jsx(ShareHeart, {
                            fontSize: "20px",
                            color: Decorative.icon.orange
                        })
                    }),
                    /*#__PURE__*/ _jsxs(Typography, {
                        variant: "paragraphSansRegular",
                        children: [
                            /*#__PURE__*/ _jsx("b", {
                                children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                                    id: "dRCT7k",
                                    defaultMessage: [
                                        {
                                            type: 0,
                                            value: "Production"
                                        }
                                    ]
                                })
                            }),
                            /*#__PURE__*/ _jsx("br", {}),
                            /*#__PURE__*/ _jsx(StrictLocalMsg, {
                                id: "Z7bHH+",
                                defaultMessage: [
                                    {
                                        type: 0,
                                        value: "Products are created with ethical sourcing, low-impact transport, renewable energy, fair trade, or cruelty-free methods."
                                    }
                                ]
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(ResponsiveBottomContainer, {
                children: /*#__PURE__*/ _jsx(Typography, {
                    variant: "labelSansRegular",
                    children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                        id: "BVETn/",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "This set of criteria has been developed in partnership with Bluebird (powered by rePurpose Global), a software solution that helps brands decarbonize and reduce waste from their products."
                            }
                        ]
                    })
                })
            })
        ]
    });
};
export var EcoFriendlyC2PageBanner = function() {
    var _React_useContext = React.useContext(Context), layoutElement = _React_useContext.layoutElement, onCTAClick = _React_useContext.onCTAClick, requestId = _React_useContext.requestId;
    var _useViewport = useViewport(), isTabletAndAbove = _useViewport.isTabletAndAbove, isDesktopAndAbove = _useViewport.isDesktopAndAbove;
    var _useState = _sliced_to_array(useState(true), 2), collapsed = _useState[0], setCollapsed = _useState[1];
    var sliderRef = useRef(null);
    var isSSR = useIsSSR();
    var imageTextTiles = layoutElement.layout_element_data.map(function(i) {
        return i.image_text_tile;
    }).filter(isNotUndefined).filter(function(i) {
        var _i_image;
        return (_i_image = i.image) === null || _i_image === void 0 ? void 0 : _i_image.url;
    });
    var shouldShowImageSlider = imageTextTiles.length > 0 && (isDesktopAndAbove || !collapsed);
    var _useMilestone = useMilestone(HAS_SEEN_ECO_FRIENDLY_CATEGORY_PAGE), hasSeenEcoFriendlyCategoryPage = _useMilestone.hasOccurred, setHasSeenEcoFriendlyCategoryPage = _useMilestone.setHasOccurred;
    var onClick = function() {
        var _layoutElement_on_click_event_name;
        onCTAClick === null || onCTAClick === void 0 ? void 0 : onCTAClick((_layoutElement_on_click_event_name = layoutElement.on_click_event_name) !== null && _layoutElement_on_click_event_name !== void 0 ? _layoutElement_on_click_event_name : "EcoFriendlyC2PageBanner", requestId);
        if (collapsed) {
            trackEcoFriendlyDefinitionBannerShowMoreButtonClick();
        }
        setCollapsed(!collapsed);
    };
    useEffect(function() {
        if (!hasSeenEcoFriendlyCategoryPage && isTabletAndAbove) {
            setCollapsed(false);
            setHasSeenEcoFriendlyCategoryPage();
        }
    }, [
        hasSeenEcoFriendlyCategoryPage,
        setHasSeenEcoFriendlyCategoryPage,
        isTabletAndAbove
    ]);
    if (isUndefined(layoutElement.main_text) || isUndefined(layoutElement.description_text)) {
        return null;
    }
    return /*#__PURE__*/ _jsxs(Wrapper, {
        children: [
            /*#__PURE__*/ _jsxs(Content, {
                children: [
                    getBannerHeaderMessage(layoutElement.main_text, layoutElement.description_text),
                    /*#__PURE__*/ _jsx(Spacer, {
                        height: isTabletAndAbove ? "3x" : "2x"
                    }),
                    getBannerBulletMessageUpdate(collapsed),
                    /*#__PURE__*/ _jsx(StyledButton, {
                        variant: "tertiary",
                        onClick: onClick,
                        children: collapsed ? /*#__PURE__*/ _jsx(StrictLocalMsg, {
                            id: "Egjo5m",
                            defaultMessage: [
                                {
                                    type: 0,
                                    value: "Show more"
                                }
                            ]
                        }) : /*#__PURE__*/ _jsx(StrictLocalMsg, {
                            id: "bMEYt5",
                            defaultMessage: [
                                {
                                    type: 0,
                                    value: "Hide"
                                }
                            ]
                        })
                    })
                ]
            }),
            shouldShowImageSlider ? /*#__PURE__*/ _jsx(EcoFriendlyC2PageImageSlider, {
                tiles: imageTextTiles,
                sliderRef: sliderRef,
                isSSR: isSSR
            }) : null
        ]
    });
};
var Wrapper = styled(Flex).attrs({
    direction: {
        mobileAndAbove: "column-reverse",
        desktopAndAbove: "row"
    }
}).withConfig({
    componentId: "sc-eddddba-0"
})([
    "margin-bottom:",
    ";"
], getSpacing("3x"));
var Content = styled(Column).withConfig({
    componentId: "sc-eddddba-1"
})([
    "background-color:",
    ";width:100%;padding:",
    ";",
    "{padding:",
    ";}",
    "{padding:",
    ";}",
    "{margin-bottom:",
    ";}"
], Decorative.tint.orange, getSpacing("6x"), responsiveMediaQueries.tabletAndBelow, getSpacing("3x", "4x"), responsiveMediaQueries.mobile, getSpacing("3x"), responsiveMediaQueries.desktopAndAbove, getSpacing("3x"));
var StyledButton = styled(Button).withConfig({
    componentId: "sc-eddddba-2"
})([
    "align-self:flex-start;"
]);
var StyledTypography = styled(Typography).attrs({
    variant: "paragraphSansRegular"
}).withConfig({
    componentId: "sc-eddddba-3"
})([
    "b{",
    "}"
], TypographyStyles({
    variant: "paragraphSansMedium"
}));
var AnimatedComponent = styled.div.withConfig({
    componentId: "sc-eddddba-4"
})([
    "visibility:",
    ";transition:max-height 0.5s ease-out,visibility 0.5s ease-out,opacity 0.5s ease-out;opacity:",
    ";max-height:",
    ";"
], function(props) {
    return props.$collapsed ? "hidden" : "visible";
}, function(props) {
    return props.$collapsed ? "0" : "1";
}, function(props) {
    return props.$collapsed ? "0" : "1000px";
});
var IconWrapper = styled.div.withConfig({
    componentId: "sc-eddddba-5"
})([
    "display:flex;align-items:center;justify-content:center;width:40px;height:40px;margin-right:",
    ";margin-bottom:",
    ";border-radius:50%;background-color:",
    ";padding:0 10px;"
], getSpacing("2x"), getSpacing("1x"), Decorative.tint.neutral);
var ResponsiveContainer = styled.div.withConfig({
    componentId: "sc-eddddba-6"
})([
    "display:flex;flex-direction:row;margin-bottom:",
    ";",
    "{display:flex;flex-direction:column;margin-bottom:10px;}"
], getSpacing("2x"), responsiveMediaQueries.mobile);
var ResponsiveBottomContainer = styled.div.withConfig({
    componentId: "sc-eddddba-7"
})([
    "margin-left:",
    ";margin-bottom:",
    ";margin-top:",
    ";",
    "{margin-left:0;}"
], getSpacing("7x"), getSpacing("3x"), getSpacing("3x"), responsiveMediaQueries.mobile);
